import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import {
  useBlockedOrderGroup,
  useBlockedOrderSource,
  type BlockedOrderGroupDetail,
} from '@/hooks/queries/credit/useBlockedOrders'
import { useLocale } from '@/hooks/useLocale'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { NO_DATA_AVAILABLE_SYMBOL } from '@/lib/utils/constants'
import { formatCurrency } from '@/lib/utils/numbers'
import { getRiskClassName, isBlockedOrderReasonCode, isRiskClassCode } from '@repo/constants'
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowUpRightIcon } from 'lucide-react'
import { BlockedOrderExceptionBanner } from '../-components/BlockedOrderExceptionBanner'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREDIT_ROUTES, toStaticData } from '../../credit-route-config'
import { BlockedOrderCustomerPanel } from '../../inbox/-components/BlockedOrderCustomerPanel'
import { BlockedOrderBatchBar } from '../-components/BlockedOrderBatchBar'
import { BlockedOrderCreditExposure } from '../-components/BlockedOrderCreditExposure'
import { BlockedOrderCutoffCell } from '../-components/BlockedOrderCutoffCell'
import { BlockedOrderDoaBand } from '../-components/BlockedOrderDoaBand'
import { BlockedOrderPayerName } from '../-components/BlockedOrderPayerName'
import {
  BlockedOrderSapHandoffBadge,
  BlockedOrderSapHandoffPill,
  BlockedOrderSapRetryButton,
} from '../-components/BlockedOrderSapHandoffBadge'
import { BlockedOrderSapPayloadButton } from '../-components/BlockedOrderSapPayloadDialog'
import { BlockedOrderStatusBadge } from '../-components/BlockedOrderStatusBadge'
import { toCutoffCountdown } from '../-data/cutoffCountdown'
import { useProvinceLabel } from '../-components/useProvinceLabel'
import { BlockedOrderWaitingOn } from '../-components/BlockedOrderWaitingOn'

export const Route = createFileRoute('/_auth/_modules/credit/blocked-orders/groups/$groupKey')({
  staticData: toStaticData(CREDIT_ROUTES['/credit/blocked-orders/groups/$groupKey']),
  component: RouteComponent,
})

/**
 * One cockpit group (CT-8): a payer's orders awaiting review, those awaiting approval, or the orders
 * one decision released or rejected. Laid out like an order's own page, but for the group — what
 * its orders share at the top (CEB, PCE%, band), the orders below at full width, the payer last.
 *
 * A pending group is decided here: whoever holds an order's band can tick it, and the footer
 * releases or rejects what is ticked. A decided group is a record of that decision — every figure as
 * captured when it was taken, beside the payer's orders that were still pending at that moment.
 */
function RouteComponent() {
  const { groupKey } = Route.useParams()
  const groupQuery = useBlockedOrderGroup(groupKey)
  const { data: sourceState } = useBlockedOrderSource()
  const navigate = useNavigate()
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set())

  if (groupQuery.isPending) return <PageLoader />
  if (groupQuery.error || !groupQuery.data) return <PageError />

  const group = groupQuery.data
  const isDecided = group.kind === 'decided'
  const tickable = group.orders.filter((order) => order.canAct)
  // Only what is still on offer: an order decided since it was ticked drops out of the selection.
  const ticked = tickable.filter((order) => checked.has(order.caseId))
  const panelCaseId = group.orders[0]?.caseId

  // A pending group whose orders have all been decided or moved on — by someone else, or before this
  // page was opened. Nothing to act on, so no status badge and no figures, only where they went.
  if (!isDecided && group.orderCount === 0) return <NothingPending group={group} />

  // The decision emptied the group: show what was just decided rather than an empty page. A review
  // marked reviewed decides nothing yet, so its orders are followed to the approval step instead.
  const onDecided = (appliedCaseIds: string[], decisionGroupKey: string | null) => {
    setChecked(new Set())
    const applied = new Set(appliedCaseIds)
    if (!group.orders.every((order) => applied.has(order.caseId))) return
    const nextKey =
      decisionGroupKey ?? (group.kind === 'pending_review' ? `approval.${group.payerErp}` : null)
    if (nextKey) {
      void navigate({
        to: '/credit/blocked-orders/groups/$groupKey',
        params: { groupKey: nextKey },
        replace: true,
      })
    }
  }

  // A move between the chains takes the whole payer, so this group is empty afterwards: follow the
  // orders to the group they wait in now rather than show the empty page.
  const onMovedBetweenChains = (result: {
    movedCaseIds: string[]
    nextGroupKey: string | null
  }) => {
    setChecked(new Set())
    if (!result.nextGroupKey) return
    void navigate({
      to: '/credit/blocked-orders/groups/$groupKey',
      params: { groupKey: result.nextGroupKey },
      replace: true,
    })
  }

  // What was sent to SAP, for a decided group on the live source: test orders are never sent.
  const hasSapPayloads = isDecided && sourceState?.source === 'live'

  return (
    // A column at least the viewport's height under the app chrome, so a short group (one order,
    // a tall screen) still gets its decision bar at the bottom edge rather than floating under the
    // cards. The shell's scroll wrapper has no height of its own to measure `min-h-full` against,
    // so this takes the viewport minus the chrome, the same 109px the inbox's two-pane page uses:
    // MainHeader (h-16) + SecondaryHeader (h-11) + the app header's bottom border.
    <div className="flex min-h-[calc(100svh-109px)] flex-col">
      <PageContainer maxWidth="wide" className="flex-1">
        <VStack gap="lg" align="stretch">
          <Spread align="start" className="gap-4">
            <VStack gap="xs" align="start">
              <HStack gap="sm" align="center" className="flex-wrap">
                <PageHeader>
                  <BlockedOrderPayerName
                    payerErp={group.payerErp}
                    payerName={group.payerName}
                    hasPayerRecord={group.hasPayerRecord}
                    variant="header"
                  />
                </PageHeader>
                <BlockedOrderStatusBadge status={group.status} pendingWith={[]} />
                <BlockedOrderSapHandoffPill outcome={group.sapOutcome} />
              </HStack>
              <HStack gap="sm" align="center" className="flex-wrap">
                {group.hasPayerRecord ? (
                  <Link
                    to="/credit/customers/$erp"
                    params={{ erp: group.payerErp }}
                    className="text-primary hover:text-primary/80 decoration-primary/30 hover:decoration-primary inline-flex items-center gap-1 font-mono underline underline-offset-4 transition-colors"
                  >
                    {group.payerErp}
                    <ArrowUpRightIcon className="size-3 shrink-0" />
                  </Link>
                ) : (
                  <Text tone="muted" className="font-mono">
                    {group.payerErp}
                  </Text>
                )}
              </HStack>
            </VStack>
            {hasSapPayloads && <BlockedOrderSapPayloadButton groupKey={group.key} />}
          </Spread>

          {/* Where the group is, for an exception payer's orders: the inbox's strip, here too. */}
          {!isDecided && group.isExceptionRouted && (
            <BlockedOrderExceptionBanner canAct={tickable.length > 0} />
          )}

          {/* Everything takes the page's full width, so the tables' columns fit without a sideways
            scroll: the orders, what else was pending, then the payer in two columns. */}
          <GroupSummaryCard group={group} />
          <GroupOrdersCard group={group} checked={checked} onCheckedChange={setChecked} />
          {isDecided && <PendingAtDecisionCard orders={group.pendingAtDecision} />}
          {panelCaseId !== undefined && (
            <BlockedOrderCustomerPanel
              erp={group.payerErp}
              caseId={panelCaseId}
              asOf={group.decision?.decidedAt}
              layout="columns"
            />
          )}
        </VStack>
      </PageContainer>
      {/* The decision bar, a sibling of the page as the CLR page's footer is: a full-bleed band
          stuck to the bottom of the scrolling area, its content centred in the same column the CLR
          footer uses so the buttons sit near the middle of a wide screen rather than at its edge.
          Not inside the container with negative margins — sticky positions the margin box, so a
          negative bottom margin hangs that much of the bar under the viewport while scrolling and
          only shows it at the end. */}
      {!isDecided && tickable.length > 0 && (
        <BlockedOrderBatchBar
          orders={ticked}
          tickableOrders={tickable}
          onClear={() => setChecked(new Set())}
          onDecided={onDecided}
          // The viewer holds a step of this exception group: any order of it addresses the send.
          exceptionCaseId={group.isExceptionRouted ? (tickable[0]?.caseId ?? null) : null}
          // An exception payer's group in the normal chain: any order of it addresses the way back.
          returnToExceptionCaseId={
            group.isExceptionPayer && !group.isExceptionRouted
              ? (tickable[0]?.caseId ?? null)
              : null
          }
          onSentToDoa={onMovedBetweenChains}
          onReturnedToException={onMovedBetweenChains}
          className="z-20 px-8 py-3"
          contentClassName="mx-auto max-w-4xl"
        />
      )}
    </div>
  )
}

/** A pending group with nothing left in it, and links to where its orders went. */
function NothingPending(props: { group: BlockedOrderGroupDetail }) {
  const { t } = useTranslation(['credit', 'common'])
  const { group } = props
  const kind = group.kind === 'pending_approval' ? 'pending_approval' : 'pending_review'
  const otherKind = kind === 'pending_review' ? 'pending_approval' : 'pending_review'

  return (
    <PageContainer maxWidth="wide">
      <VStack gap="lg" align="stretch">
        <VStack gap="xs" align="start">
          <PageHeader>
            <BlockedOrderPayerName
              payerErp={group.payerErp}
              payerName={group.payerName}
              hasPayerRecord={group.hasPayerRecord}
              variant="header"
            />
          </PageHeader>
          {group.hasPayerRecord ? (
            <Link
              to="/credit/customers/$erp"
              params={{ erp: group.payerErp }}
              className="text-primary hover:text-primary/80 decoration-primary/30 hover:decoration-primary inline-flex items-center gap-1 font-mono underline underline-offset-4 transition-colors"
            >
              {group.payerErp}
              <ArrowUpRightIcon className="size-3 shrink-0" />
            </Link>
          ) : (
            <Text tone="muted" className="font-mono">
              {group.payerErp}
            </Text>
          )}
        </VStack>
        <Card>
          <CardContent className="p-6">
            <VStack gap="md" align="start">
              <VStack gap="xs" align="start">
                <Text weight="medium">{t(`blockedOrders.groups.nothingPending.${kind}`)}</Text>
                <Text tone="muted">{t('blockedOrders.groups.nothingPending.description')}</Text>
              </VStack>
              <HStack gap="sm" align="center" className="flex-wrap">
                {group.lastDecisionGroupKey && (
                  <Button type="button" size="sm" asChild>
                    <Link
                      to="/credit/blocked-orders/groups/$groupKey"
                      params={{ groupKey: group.lastDecisionGroupKey }}
                    >
                      {t('blockedOrders.groups.openLastDecision')}
                    </Link>
                  </Button>
                )}
                {group.otherPendingGroupKey && (
                  <Button type="button" size="sm" variant="secondary" asChild>
                    <Link
                      to="/credit/blocked-orders/groups/$groupKey"
                      params={{ groupKey: group.otherPendingGroupKey }}
                    >
                      {t(`blockedOrders.groups.openOtherPending.${otherKind}`)}
                    </Link>
                  </Button>
                )}
                <Button type="button" size="sm" variant="ghost" asChild>
                  <Link to="/credit/blocked-orders/pending">
                    {t('blockedOrders.groups.backToCockpit')}
                  </Link>
                </Button>
              </HStack>
            </VStack>
          </CardContent>
        </Card>
      </VStack>
    </PageContainer>
  )
}

/** What every order of the group shares, and for a decided group, the decision itself. */
function GroupSummaryCard(props: { group: BlockedOrderGroupDetail }) {
  const { t } = useTranslation(['credit', 'common'])
  const locale = useLocale()
  const { formatDateWithTime } = useLocalizedDate()
  const { group } = props
  const dash = NO_DATA_AVAILABLE_SYMBOL
  const money = (value: number | null) => (value === null ? dash : formatCurrency(locale, value))
  const { decision } = group

  return (
    <Card>
      <CardContent className="p-4">
        <VStack gap="md" align="stretch">
          <HStack gap="lg" align="start" className="flex-wrap">
            <Metric
              label={t('blockedOrders.groups.columns.totalAmount')}
              value={money(group.totalAmount)}
              mono
            />
            <Metric label={t('blockedOrders.metrics.ceb')} value={money(group.ceb)} mono />
            <Metric
              label={t('blockedOrders.cockpit.columns.pcePercent')}
              value={
                group.pcePercent === null
                  ? dash
                  : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(group.pcePercent)}%`
              }
              mono
            />
            <VStack gap="xs" align="start">
              <Text size="xs" tone="muted">
                {t('blockedOrders.cockpit.columns.doaBand')}
              </Text>
              {group.orderCount === 0 ? (
                <Text weight="medium">{dash}</Text>
              ) : (
                <BlockedOrderDoaBand
                  doaOptionKey={group.doaOptionKey}
                  isExceptionRouted={group.isExceptionRouted}
                  hasMixedBands={group.hasMixedBands}
                />
              )}
            </VStack>
            <Metric
              label={t('blockedOrders.cockpit.columns.riskClass')}
              value={
                group.currentRiskClass && isRiskClassCode(group.currentRiskClass)
                  ? t(`riskClass.${getRiskClassName(group.currentRiskClass)}`)
                  : (group.currentRiskClass ?? dash)
              }
            />
            <Metric
              label={t('blockedOrders.cockpit.columns.creditLimit')}
              value={money(group.currentCreditLimit)}
              mono
            />
            <BlockedOrderCreditExposure exposure={group.exposure} />
            {group.kind !== 'decided' && (
              <VStack gap="xs" align="start">
                <Text size="xs" tone="muted">
                  {t('blockedOrders.metrics.cutoff')}
                </Text>
                <BlockedOrderCutoffCell countdown={toCutoffCountdown(group.cutoffAt, new Date())} />
              </VStack>
            )}
          </HStack>
          {decision && (
            <VStack gap="xs" align="start" className="border-t pt-3">
              <Text tone="muted">
                {decision.decidedBy
                  ? t('blockedOrders.decisionBatches.decidedBy', {
                      name: decision.decidedBy,
                      date: formatDateWithTime(decision.decidedAt),
                    })
                  : t('blockedOrders.decisionBatches.decidedOn', {
                      date: formatDateWithTime(decision.decidedAt),
                    })}
              </Text>
              {decision.comment && <Text className="whitespace-pre-wrap">{decision.comment}</Text>}
              {/* A committee release: everyone who signed, not only the last signature. */}
              {group.approvals.length > 1 && (
                <VStack gap="xs" align="start">
                  <Text size="xs" tone="muted">
                    {t('blockedOrders.decisionBatches.approvals')}
                  </Text>
                  {group.approvals.map((approval) => (
                    <Text key={`${approval.approvedBy}-${approval.approvedAt.getTime()}`}>
                      {approval.approvedBy
                        ? t('blockedOrders.decisionBatches.decidedBy', {
                            name: approval.approvedBy,
                            date: formatDateWithTime(approval.approvedAt),
                          })
                        : t('blockedOrders.decisionBatches.decidedOn', {
                            date: formatDateWithTime(approval.approvedAt),
                          })}
                    </Text>
                  ))}
                </VStack>
              )}
            </VStack>
          )}
        </VStack>
      </CardContent>
    </Card>
  )
}

/**
 * The group's orders. Over credit limit, PCE% and band are the group's, shown above, so they are
 * not repeated per order. A pending group counts each order down to its cutoff and offers a tick where the viewer
 * holds the order's band.
 */
function GroupOrdersCard(props: {
  group: BlockedOrderGroupDetail
  checked: ReadonlySet<string>
  onCheckedChange: (checked: ReadonlySet<string>) => void
}) {
  const { t } = useTranslation('credit')
  const provinceLabel = useProvinceLabel()
  const locale = useLocale()
  const { formatDateWithTime } = useLocalizedDate()
  const { group } = props
  const dash = NO_DATA_AVAILABLE_SYMBOL
  const now = new Date()
  const isPending = group.kind !== 'decided'
  const tickableIds = group.orders.filter((order) => order.canAct).map((order) => order.caseId)
  const hasTicks = isPending && tickableIds.length > 0
  const checkedCount = tickableIds.filter((id) => props.checked.has(id)).length

  const setChecked = (caseIds: string[], isChecked: boolean) => {
    const next = new Set(props.checked)
    for (const id of caseIds) {
      if (isChecked) next.add(id)
      else next.delete(id)
    }
    props.onCheckedChange(next)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('blockedOrders.groups.orders', { count: group.orders.length })}</CardTitle>
      </CardHeader>
      <CardContent>
        {group.orders.length === 0 ? (
          <Text tone="muted">{t('blockedOrders.groups.empty')}</Text>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {hasTicks && (
                  <TableHead className="w-0">
                    <Checkbox
                      checked={
                        checkedCount === 0
                          ? false
                          : checkedCount === tickableIds.length
                            ? true
                            : 'indeterminate'
                      }
                      onCheckedChange={() =>
                        setChecked(tickableIds, checkedCount < tickableIds.length)
                      }
                      aria-label={t('blockedOrders.batch.selectAll')}
                    />
                  </TableHead>
                )}
                {isPending && <TableHead>{t('blockedOrders.cockpit.columns.cutoff')}</TableHead>}
                {isPending && <TableHead>{t('blockedOrders.groups.columns.waitingOn')}</TableHead>}
                {!isPending && <TableHead>{t('blockedOrders.sap.column')}</TableHead>}
                <TableHead>{t('blockedOrders.cockpit.columns.caseId')}</TableHead>
                <TableHead>{t('blockedOrders.columns.order')}</TableHead>
                <TableHead className="text-right">
                  {t('blockedOrders.cockpit.columns.amount')}
                </TableHead>
                <TableHead>{t('blockedOrders.columns.soldTo')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.province')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.poNumber')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.referenceNumber')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.caseTitle')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.reason')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.companyCode')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.caseCreatedOn')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.orders.map((order) => (
                <TableRow key={order.caseId}>
                  {hasTicks && (
                    <TableCell>
                      {order.canAct && (
                        <Checkbox
                          checked={props.checked.has(order.caseId)}
                          onCheckedChange={(isChecked) =>
                            setChecked([order.caseId], isChecked === true)
                          }
                          aria-label={t('blockedOrders.batch.selectOrder', {
                            order: order.salesDocument,
                          })}
                        />
                      )}
                    </TableCell>
                  )}
                  {isPending && (
                    <TableCell className="whitespace-nowrap">
                      <BlockedOrderCutoffCell countdown={toCutoffCountdown(order.cutoffAt, now)} />
                    </TableCell>
                  )}
                  {isPending && (
                    <TableCell>
                      <BlockedOrderWaitingOn
                        pendingWith={order.pendingWith}
                        committee={order.committee}
                      />
                    </TableCell>
                  )}
                  {!isPending && (
                    <TableCell>
                      <HStack gap="xs" align="center" className="whitespace-nowrap">
                        <BlockedOrderSapHandoffBadge handoff={order.sapHandoff} />
                        <BlockedOrderSapRetryButton
                          caseId={order.caseId}
                          handoff={order.sapHandoff}
                        />
                      </HStack>
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap font-mono">{order.caseId}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="font-mono">{order.salesDocument}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right font-mono tabular-nums">
                    {order.amount === null ? dash : formatCurrency(locale, order.amount)}
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate">
                    {order.soldToName ?? order.soldToErp ?? dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {provinceLabel(order.province) ?? dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono">
                    {order.poNumber ?? dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono">
                    {order.referenceNumber ?? dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{order.caseType ?? dash}</TableCell>
                  <TableCell className="min-w-[160px]">
                    {order.reason
                      ? isBlockedOrderReasonCode(order.reason)
                        ? t(`blockedOrders.reasons.${order.reason}`)
                        : order.reason
                      : dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{order.companyCode ?? dash}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.createdAtSap ? formatDateWithTime(order.createdAtSap) : dash}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The payer's orders that were still in their chain when this decision was taken, as they stood
 * then — what else the decider had in front of them. Frozen.
 */
function PendingAtDecisionCard(props: { orders: BlockedOrderGroupDetail['pendingAtDecision'] }) {
  const { t } = useTranslation('credit')
  const provinceLabel = useProvinceLabel()
  const locale = useLocale()
  const { formatDateWithTime } = useLocalizedDate()
  const dash = NO_DATA_AVAILABLE_SYMBOL

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('blockedOrders.groups.pendingAtDecision.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {props.orders.length === 0 ? (
          <Text tone="muted">{t('blockedOrders.groups.pendingAtDecision.empty')}</Text>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('blockedOrders.columns.order')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.poNumber')}</TableHead>
                <TableHead>{t('blockedOrders.columns.soldTo')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.province')}</TableHead>
                <TableHead>{t('blockedOrders.cockpit.columns.caseCreatedOn')}</TableHead>
                <TableHead className="text-right">
                  {t('blockedOrders.cockpit.columns.amount')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {props.orders.map((order) => (
                <TableRow key={order.caseId}>
                  <TableCell className="whitespace-nowrap">
                    <span className="font-mono">{order.salesDocument}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono">
                    {order.poNumber ?? dash}
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate">
                    {order.soldToName ?? order.soldToErp ?? dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {provinceLabel(order.province) ?? dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {order.createdAtSap ? formatDateWithTime(order.createdAtSap) : dash}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right font-mono tabular-nums">
                    {order.amount === null ? dash : formatCurrency(locale, order.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function Metric(props: { label: string; value: string; mono?: boolean }) {
  return (
    <VStack gap="xs" align="start">
      <Text size="xs" tone="muted">
        {props.label}
      </Text>
      <Text weight="medium" monospace={props.mono} className={props.mono ? 'tabular-nums' : ''}>
        {props.value}
      </Text>
    </VStack>
  )
}
